/**
 * useSharePrompts — fetch pending share prompts for a producer.
 * Enriches raw DB rows with event titles and display copy.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getPromptCopy } from './sharePromptsCopy';
import type { SharePrompt, EnrichedSharePrompt, SharePromptStatus } from './types';

const PROMPTS_KEY = 'share-prompts';

async function fetchPendingPrompts(producerId: string): Promise<EnrichedSharePrompt[]> {
  // Fetch pending prompts
  const { data: prompts, error } = await supabase
    .from('share_prompts')
    .select('*')
    .eq('producer_id', producerId)
    .eq('status', 'pending')
    .order('created_at', { ascending: false })
    .limit(10);

  if (error) throw error;
  if (!prompts || prompts.length === 0) return [];

  // Collect event IDs to enrich
  const eventIds = prompts
    .filter((p) => p.entity_type === 'event')
    .map((p) => p.entity_id);

  // Fetch event titles in one query
  let eventMap: Record<string, string> = {};
  if (eventIds.length > 0) {
    const { data: events } = await supabase
      .from('producer_events')
      .select('id, title')
      .in('id', eventIds);

    if (events) {
      eventMap = Object.fromEntries(events.map((e) => [e.id, e.title]));
    }
  }

  // Enrich prompts with display data
  return prompts.map((raw): EnrichedSharePrompt => {
    const prompt = raw as unknown as SharePrompt;
    const entityTitle = eventMap[prompt.entity_id] ?? prompt.metadata?.title as string ?? 'Aktivitet';
    const copy = getPromptCopy(prompt.prompt_type, entityTitle);

    return {
      ...prompt,
      entityTitle,
      entityImageUrl: null, // Could be enriched later with media_assets
      headline: copy.headline,
      description: copy.description,
    };
  });
}

export function useSharePrompts(producerId: string | undefined) {
  return useQuery({
    queryKey: [PROMPTS_KEY, producerId],
    queryFn: () => fetchPendingPrompts(producerId!),
    enabled: !!producerId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useUpdatePromptStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ promptId, status }: { promptId: string; status: SharePromptStatus }) => {
      const updates: Record<string, unknown> = { status };
      if (status === 'dismissed') updates.dismissed_at = new Date().toISOString();
      if (status === 'completed') updates.completed_at = new Date().toISOString();

      const { error } = await supabase
        .from('share_prompts')
        .update(updates)
        .eq('id', promptId);

      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PROMPTS_KEY] });
    },
  });
}
